const CHEVRON_RIGHT = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>`;
const CHEVRON_LEFT  = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>`;

export function initMobileNav() {
	const gsap = window.gsap;
	if ( ! gsap ) return;

	const menu      = document.getElementById( 'mobile-menu' );
	const header    = document.getElementById( 'site-header' );
	if ( ! menu || ! header ) return;

	const hamburger = header.querySelector( '.site-header__hamburger' );
	const overlay   = menu.querySelector( '.mobile-menu__overlay' );
	const closeBtn  = menu.querySelector( '.mobile-menu__close' );
	const drawer    = menu.querySelector( '.mobile-menu__drawer' );
	const body      = menu.querySelector( '.mobile-menu__body' );

	// Set initial off-screen state
	gsap.set( drawer, { x: '100%' } );
	gsap.set( overlay, { opacity: 0 } );

	let panels;

	function ensurePanels() {
		if ( ! panels ) {
			panels = buildPanels( body, gsap );
		}

		return panels;
	}

	// ── Open / Close ──────────────────────────────────────────────────────────

	function openMenu() {
		ensurePanels();
		menu.removeAttribute( 'aria-hidden' );
		menu.classList.add( 'is-open' );
		hamburger?.setAttribute( 'aria-expanded', 'true' );
		hamburger?.classList.add( 'is-active' );
		document.body.classList.add( 'menu-open' );

		gsap.to( drawer, { x: '0%', duration: 0.48, ease: 'power2.out' } );
		gsap.to( overlay, { opacity: 1, duration: 0.32 } );
	}

	function closeMenu() {
		hamburger?.setAttribute( 'aria-expanded', 'false' );
		hamburger?.classList.remove( 'is-active' );
		document.body.classList.remove( 'menu-open' );

		gsap.to( drawer, {
			x: '100%', duration: 0.42, ease: 'power2.in',
			onComplete() {
				menu.setAttribute( 'aria-hidden', 'true' );
				menu.classList.remove( 'is-open' );
				panels?.reset();
			},
		} );
		gsap.to( overlay, { opacity: 0, duration: 0.3 } );
	}

	hamburger?.addEventListener( 'click', openMenu );
	overlay?.addEventListener( 'click', closeMenu );
	closeBtn?.addEventListener( 'click', closeMenu );
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' && menu.classList.contains( 'is-open' ) ) closeMenu();
	} );
}

// ── Multilayer panel builder ─────────────────────────────────────────────────

function buildPanels( body, gsap ) {
	const rootList = body?.querySelector( '.mobile-menu__list' );
	if ( ! rootList ) return { reset: () => {} };

	// Wrap root list in panel div
	const rootPanel = makePanel( null );
	body.insertBefore( rootPanel, rootList );
	rootPanel.appendChild( rootList );

	const allPanels  = [ rootPanel ];
	let activeIndex  = 0;

	// Recursively add expand buttons + create child panels
	processItems( rootList, 0 );

	function processItems( list, parentIndex ) {
		list.querySelectorAll( ':scope > .menu-item-has-children' ).forEach( ( item ) => {
			const link    = item.querySelector( ':scope > a' );
			const subMenu = item.querySelector( ':scope > ul' );
			if ( ! link || ! subMenu ) return;

			// Add expand chevron button next to the link
			const expandBtn = document.createElement( 'button' );
			expandBtn.className = 'mobile-menu__expand';
			expandBtn.setAttribute( 'aria-label', `${ link.textContent.trim() } submenu openen` );
			expandBtn.innerHTML = CHEVRON_RIGHT;
			link.insertAdjacentElement( 'afterend', expandBtn );

			// Build sub-panel
			const subPanel    = makePanel( link.textContent.trim() );
			const subList     = subMenu.cloneNode( true );
			subList.className = 'mobile-menu__list';
			subPanel.appendChild( subList );
			body.appendChild( subPanel );

			// The original <ul> stays in the root list otherwise — nothing hides
			// it, so its items render flat/unstyled under the parent link,
			// visible immediately on open instead of only inside their own panel.
			subMenu.remove();

			const subIndex = allPanels.length;
			allPanels.push( subPanel );
			gsap.set( subPanel, { x: '100%' } );

			expandBtn.addEventListener( 'click', () => {
				const current = allPanels[ activeIndex ];
				gsap.to( current, { x: '-100%', duration: 0.35, ease: 'power2.inOut' } );
				gsap.to( subPanel, { x: '0%', duration: 0.35, ease: 'power2.inOut' } );
				activeIndex = subIndex;
			} );

			subPanel.querySelector( '.mobile-menu__back-btn' )?.addEventListener( 'click', () => {
				const current = allPanels[ activeIndex ];
				gsap.to( current, { x: '100%', duration: 0.35, ease: 'power2.inOut' } );
				gsap.to( allPanels[ parentIndex ], { x: '0%', duration: 0.35, ease: 'power2.inOut' } );
				activeIndex = parentIndex;
			} );

			// Recurse for deeper nesting
			processItems( subList, subIndex );
		} );
	}

	return {
		reset() {
			activeIndex = 0;
			allPanels.forEach( ( panel, i ) => gsap.set( panel, { x: i === 0 ? '0%' : '100%' } ) );
		},
	};
}

function makePanel( backLabel ) {
	const panel = document.createElement( 'div' );
	panel.className = 'mobile-menu__panel';

	if ( backLabel ) {
		const header = document.createElement( 'div' );
		header.className = 'mobile-menu__panel-header';
		header.innerHTML = `
			<button class="mobile-menu__back-btn" aria-label="Terug naar hoofdmenu">
				${ CHEVRON_LEFT }
				<span>${ backLabel }</span>
			</button>`;
		panel.appendChild( header );
	}

	return panel;
}
